"use client"

import { usePuck } from "@puckeditor/core"
import { HttpTypes } from "@medusajs/types"
import { usePuckContext } from "@/components/puck/PuckContextProvider"

export interface OrderData {
  order?: HttpTypes.StoreOrder
  countryCode?: string
  currencyCode?: string
  regionId?: string
}

/**
 * Hook to access order context from Puck — mirrors `useProduct()`.
 *
 * Reads the order injected into the Puck `context` by `PuckRenderer`
 * (`data.context = { order, countryCode, currencyCode, regionId }`).
 *
 * Works in both surfaces:
 *  - Editor: reads `usePuck().appState.data.context`.
 *  - Storefront: falls back to `usePuckContext()` (our custom provider).
 *
 * Returns `order: undefined` when not inside a provider (e.g. editor with no
 * sample order) so components fall back to their placeholder/mock rendering.
 */
export function useOrderData(): OrderData {
  // Try Puck editor context first.
  try {
    const { appState } = usePuck()
    const context = appState.data?.context as OrderData | undefined
    return {
      order: context?.order,
      countryCode: context?.countryCode,
      currencyCode: context?.currencyCode,
      regionId: context?.regionId,
    }
  } catch {
    // Storefront custom context.
    const { context } = usePuckContext()
    const orderContext = context as OrderData | undefined
    return {
      order: orderContext?.order,
      countryCode: orderContext?.countryCode,
      currencyCode: orderContext?.currencyCode,
      regionId: orderContext?.regionId,
    }
  }
}
